/**
 * @module リクエストApi_コンポーネント
 */
import Axios from 'axios';

export const axios = Axios.create({
  baseURL: process.env.NODE_ENV === 'local' ? process.env.VUE_APP_BASE_URL : '',
});

const state = {
  code: '',
  data: '',
  // API実行時のタイムアウト処理を定義
  timeout: 60 * 1000,
  interruptionFlag: false,
};

const getters = {
  getApiInfoStatus(state) {
    return state.code;
  },
  getApiInfo(state) {
    return state.data;
  },
  postApiInfo(state) {
    return state.data;
  },
  putApiInfo(state) {
    return state.data;
  },
  getInterruptionFlag(state) {
    return state.interruptionFlag;
  },
};

const mutations = {
  getApiInfo(state, payload) {
    state.data = payload.data;
    state.code = payload.code;
  },
  postApiInfo(state, payload) {
    state.data = payload.data;
  },
  putApiInfo(state, payload) {
    state.data = payload.data;
  },
  setInterruptionFlag(state, val) {
    state.interruptionFlag = val;
  },
};

const createRequestConfig = (context) => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  let csrfToken = context.rootGetters.csrfToken;

  if (!csrfToken) {
    csrfToken = '';
  }

  config.headers['csrf-token'] = csrfToken;

  return config;
};

const extractCsrfToken = (headers) => {
  if (headers['csrf-token']) {
    return headers['csrf-token'];
  } else {
    return '';
  }
};

const actions = {
  /************************************************************************************************/
  // APIで情報（GET）を取得のための処理
  // API取得するのを待ってから、mutationsにcommit
  /************************************************************************************************/
  // 【引数】
  // context : ストアインスタンスのメソッドやプロパティのセットと同じものを呼び出せるコンテキストオブジェクト
  // payload : vuex呼び出し元よりdispatchされた際に受け取る引数
  /************************************************************************************************/
  async getApiInfoAction(context, payload) {
    const config = createRequestConfig(context);

    const payload_data = {
      code: '',
      data: '',
    };
    // APIを投げて、正常時：resolve、異常時：rejectを投げる
    // API投下後、最終的にmutationsにcommitをする
    return await new Promise((resolve, reject) => {
      axios
        .get(
          payload.url,
          {
            params: payload.params,
            timeout: state.timeout,
          },
          config
        )
        .then((response) => {
          payload_data.code = response.status;
          payload_data.data = response.data;
          resolve(response);
        })
        .catch((error) => {
          payload_data.code = error.status;
          payload_data.data = error;
          reject(error);
        })
        .finally(() => {
          context.commit('getApiInfo', payload_data);
        });
    });
  },
  /************************************************************************************************/
  // APIで情報（POST）を取得のための処理
  // API取得するのを待ってから、mutationsにcommit
  /************************************************************************************************/
  // 【引数】
  // context : ストアインスタンスのメソッドやプロパティのセットと同じものを呼び出せるコンテキストオブジェクト
  // payload : vuex呼び出し元よりdispatchされた際に受け取る引数
  /************************************************************************************************/
  async postApiInfoAction(context, payload) {
    const config = createRequestConfig(context);

    const payload_data = {
      data: '',
    };
    // APIを投げて、正常時：resolve、異常時：rejectを投げる
    // API投下後、最終的にmutationsにcommitをする
    return await new Promise((resolve, reject) => {
      axios
        .post(payload.url, payload.params, config)
        .then((response) => {
          payload_data.data = response.data;
          context.commit(
            'setCsrfToken',
            { csrfToken: extractCsrfToken(response.headers) },
            { root: true }
          );
          resolve(response);
        })
        .catch((error) => {
          payload_data.data = error;
          if (error.response && error.response.status == 400) {
            context.commit(
              'setCsrfToken',
              { csrfToken: extractCsrfToken(error.response.headers) },
              { root: true }
            );
          }
          reject(error);
        })
        .finally(() => {
          context.commit('postApiInfo', payload_data);
        });
    });
  },
  /************************************************************************************************/
  // APIで情報を削除するための処理
  /************************************************************************************************/
  // 【引数】
  // context : ストアインスタンスのメソッドやプロパティのセットと同じものを呼び出せるコンテキストオブジェクト
  // payload : vuex呼び出し元よりdispatchされた際に受け取る引数
  /************************************************************************************************/
  async deleteApiInfoAction(context, payload) {
    const config = createRequestConfig(context);

    const payload_data = {
      data: '',
    };
    return await new Promise((resolve, reject) => {
      axios
        .delete(
          payload.url,
          {
            data: payload.params,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            timeout: state.timeout,
          },
          config
        )
        .then((response) => {
          payload_data.data = response.data;
          context.commit(
            'setCsrfToken',
            { csrfToken: extractCsrfToken(response.headers) },
            { root: true }
          );
          resolve(response);
        })
        .catch((error) => {
          payload_data.data = error;
          if (error.response && error.response.status == 400) {
            context.commit(
              'setCsrfToken',
              { csrfToken: extractCsrfToken(error.response.headers) },
              { root: true }
            );
          }
          reject(error);
        })
        .finally(() => {
          context.commit('getApiInfo', payload_data);
        });
    });
  },
  /************************************************************************************************/
  // APIで情報を更新するための処理
  /************************************************************************************************/
  // 【引数】
  // context : ストアインスタンスのメソッドやプロパティのセットと同じものを呼び出せるコンテキストオブジェクト
  // payload : vuex呼び出し元よりdispatchされた際に受け取る引数
  /************************************************************************************************/
  async putApiInfoAction(context, payload) {
    const config = createRequestConfig(context);

    const payload_data = {
      data: '',
    };
    return await new Promise((resolve, reject) => {
      axios
        .put(
          payload.url,
          {
            data: payload.params,
            timeout: state.timeout,
          },
          config
        )
        .then((response) => {
          payload_data.data = response.data;
          context.commit(
            'setCsrfToken',
            { csrfToken: extractCsrfToken(response.headers) },
            { root: true }
          );
          resolve(response);
        })
        .catch((error) => {
          payload_data.data = error;
          if (error.response && error.response.status == 400) {
            context.commit(
              'setCsrfToken',
              { csrfToken: extractCsrfToken(error.response.headers) },
              { root: true }
            );
          }
          reject(error);
        })
        .finally(() => {
          context.commit('putApiInfo', payload_data);
        });
    });
  },
  /************************************************************************************************/
  // APP_CO9915
  // 結果が取得できるまでレスポンスが返ってくる度に2秒の間を取り再送信
  /************************************************************************************************/
  // 【引数】
  // context : ストアインスタンスのメソッドやプロパティのセットと同じものを呼び出せるコンテキストオブジェクト
  // payload : vuex呼び出し元よりdispatchされた際に受け取る引数
  /************************************************************************************************/
  async postSecurityUnitExecutionResult(context, payload) {
    if (
      typeof payload.params.sendCommandKey == 'undefined' ||
      payload.params.sendCommandKey == '' ||
      typeof payload.params.sendCustomerNumber == 'undefined' ||
      payload.params.sendCustomerNumber == ''
    ) {
      return await new Promise((resolve) => {
        resolve({ contentsInfo: { securityUnitExecutionResult: -1 } });
      });
    }
    let config = '';

    const payload_data = {
      data: '',
    };

    let getFlag = false;
    let start = new Date();
    // 呼び出し元のURL
    let urlExecutedFunction = location.href;

    // 1分30秒経過するまで2秒間隔を開け再送信
    // バグで無限ループする可能性を考慮して上限を45回とする（1分30秒/2秒）
    for (let i = 0; i < 45; i++) {
      // 現在のURLが呼び出し元のURLと一致しない場合、画面遷移が発生したとみなし処理を抜ける
      if (urlExecutedFunction !== location.href) {
        return;
      }
      config = createRequestConfig(context);
      await postApi('/api/common/securityUnitExecutionResult', payload, config)
        // await postApi("post", payload, config)
        .then((response) => {
          payload_data.data = response.data;
          // サンプルデータ
          // payload_data.data.contentsInfo = { securityUnitExecutionResult: 0 };
          context.commit(
            'setCsrfToken',
            { csrfToken: extractCsrfToken(response.headers) },
            { root: true }
          );
          getFlag = true;
        })
        .catch((error) => {
          payload_data.data = error;
          if (error.response && error.response.status == 400) {
            context.commit(
              'setCsrfToken',
              { csrfToken: extractCsrfToken(error.response.headers) },
              { root: true }
            );
          }
          getFlag = false;
        });

      if (state.interruptionFlag) {
        return await new Promise((resolve) => {
          resolve({ contentsInfo: { securityUnitExecutionResult: -1 } });
        });
      }

      if (
        getFlag &&
        typeof payload_data.data.contentsInfo.securityUnitExecutionResult !== 'undefined' &&
        payload_data.data.contentsInfo.securityUnitExecutionResult != -1
      ) {
        context.commit('postApiInfo', payload_data);
        return await new Promise((resolve) => {
          resolve(payload_data.data);
        });
      }

      // 1分30秒経過したらループを終了
      let stop = new Date();
      let ms = stop.getTime() - start.getTime();
      if (ms >= 90000) {
        break;
      }

      // レスポンスが帰って来たタイミングから2秒間隔で次のループへ
      await sleep(2000);
    }

    return await new Promise((resolve) => {
      resolve({ contentsInfo: { securityUnitExecutionResult: -1 } });
    });
  },
  /************************************************************************************************/
  // APP_CO9902
  // 結果が取得できるまでレスポンスが返ってくる度に3秒の間を取り再送信
  /************************************************************************************************/
  // 【引数】
  // context : ストアインスタンスのメソッドやプロパティのセットと同じものを呼び出せるコンテキストオブジェクト
  // payload : vuex呼び出し元よりdispatchされた際に受け取る引数
  /************************************************************************************************/
  async postCameraCoordinationCheck(context, payload) {
    const config = createRequestConfig(context);

    const payload_data = {
      data: '',
    };

    let getFlag = false;
    let start = new Date();

    // 3分経過するまで3秒間隔を開け再送信
    // バグで無限ループする可能性を考慮して上限を60回とする（3分/3秒）
    for (let i = 0; i < 60; i++) {
      await postApi('/api/common/cameraCoordinationCheck', payload, config)
        .then((response) => {
          payload_data.data = response.data;
          context.commit(
            'setCsrfToken',
            { csrfToken: extractCsrfToken(response.headers) },
            { root: true }
          );
          getFlag = true;
        })
        .catch(async (error) => {
          payload_data.data = error;
          if (error.response && error.response.status == 400) {
            return await new Promise((resolve, reject) => {
              reject(error);
            });
          }

          if (error.response) {
            context.commit(
              'setCsrfToken',
              { csrfToken: extractCsrfToken(error.response.headers) },
              { root: true }
            );
          }
          getFlag = false;
        });

      if (
        getFlag &&
        typeof payload_data.data.contentsInfo.cameraCoordinationResult !== 'undefined' &&
        payload_data.data.contentsInfo.cameraCoordinationResult != 2
      ) {
        context.commit('postApiInfo', payload_data);
        return await new Promise((resolve) => {
          resolve(payload_data.data);
        });
      }

      // 3分経過したらループを終了
      let stop = new Date();
      let ms = stop.getTime() - start.getTime();
      if (ms >= 180000) {
        break;
      }

      // レスポンスが帰って来たタイミングから3秒間隔で次のループへ
      await sleep(3000);
    }

    return await new Promise((resolve) => {
      resolve({ contentsInfo: { cameraCoordinationResult: 2 } });
    });
  },
};

// postApiを実行
async function postApi(url, payload, config) {
  return await new Promise((resolve, reject) => {
    axios
      .post(url, payload.params, config)
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const requestApi = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default requestApi;
